/** Phone route panel height reserved before the panel has been measured. */
const PHONE_ROUTE_PANEL_FALLBACK_CSS = 280

export interface RouteFitBottomOcclusionArgs {
  /** The tablet / desktop layout: panels sit in the LEFT sidebar, not at the bottom. */
  readonly hasSidePanel: boolean
  /** `body.clientHeight − innerHeight` — the canvas extending below the visible viewport. */
  readonly bodyGapCss: number
  /** Same gap from `--mm-body-height` when `syncCanvasSize` already ran. */
  readonly cssVarGapCss: number
  /** `screen.height − innerHeight` — a phone URL-bar proxy for a cold first frame. */
  readonly screenGapCss: number
  /** Live heights of the bottom panels (phone RouteCard, StationCard carousel). */
  readonly bottomPanelsCss: readonly number[]
}

/**
 * CSS px the route auto-fit keeps free at the bottom of the canvas.
 *
 * The phone layout reserves its bottom route panel (a 280 px fallback until
 * measured) and the URL-bar gap (`screen.height − innerHeight` on a cold
 * first frame, TASK-275). Neither exists in the sidebar layout, where
 * `screen.height − innerHeight` is just browser chrome: reserving them there
 * left ~500 of 993 px for the route and zoomed out twice as far as needed
 * (owner 2026-10-19). A real body-vs-viewport gap still counts everywhere.
 */
export function routeFitBottomOcclusionCss(args: RouteFitBottomOcclusionArgs): number {
  let gap = Math.max(0, args.bodyGapCss)
  if (gap === 0) gap = Math.max(0, args.cssVarGapCss)
  if (gap === 0 && !args.hasSidePanel) gap = Math.max(0, args.screenGapCss)
  let occlusion = args.hasSidePanel ? gap : Math.max(PHONE_ROUTE_PANEL_FALLBACK_CSS, gap)
  for (const panel of args.bottomPanelsCss) {
    if (panel > occlusion) occlusion = panel
  }
  return occlusion
}
